import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  adminClient,
  type AdminUserSummary,
  type RoleRow,
} from '@/auth/adminClient'
import NewUserModal from './NewUserModal'
import EditUserModal from './EditUserModal'
import ManageRolesModal from './ManageRolesModal'

type Mode =
  | { kind: 'idle' }
  | { kind: 'new' }
  | { kind: 'edit'; user: AdminUserSummary }
  | { kind: 'roles'; user: AdminUserSummary }

export default function UsersView() {
  const [users, setUsers] = useState<AdminUserSummary[]>([])
  const [roles, setRoles] = useState<RoleRow[]>([])
  const [includeInactive, setIncludeInactive] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>({ kind: 'idle' })

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [u, r] = await Promise.all([
        adminClient.listUsers({ includeInactive }),
        adminClient.listRoles(),
      ])
      setUsers(u.users)
      setRoles(r.roles)
    } catch (e) {
      const status = (e as { status?: number })?.status
      setError(status === 403
        ? 'You do not have permission to view users.'
        : `Failed to load users (${String((e as Error).message ?? e)}).`)
    } finally {
      setLoading(false)
    }
  }, [includeInactive])

  useEffect(() => { void refresh() }, [refresh])

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) =>
        (a.email ?? '').toLowerCase().localeCompare((b.email ?? '').toLowerCase()),
      ),
    [users],
  )

  return (
    <div className="panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '0.5px solid var(--bd-0)' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--t-0)' }}>Users</div>
          <div style={{ fontSize: 11, color: 'var(--t-2)' }}>Manage platform user accounts and role grants.</div>
        </div>
        <label style={{ fontSize: 11, color: 'var(--t-2)', display: 'flex', alignItems: 'center', gap: 6, marginRight: 8 }}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Show inactive
        </label>
        <button onClick={() => void refresh()} style={btnGhost}>Refresh</button>
        <button onClick={() => setMode({ kind: 'new' })} style={btnPrimary}>+ New user</button>
      </header>

      {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--t-2)' }}>Loading…</div>}
      {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--crit)' }}>{error}</div>}

      {!loading && !error && (
        <div style={{ flex: 1, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-1)' }}>
              <tr style={{ textAlign: 'left', color: 'var(--t-3)', borderBottom: '0.5px solid var(--bd-0)' }}>
                <th style={th}>Email</th>
                <th style={th}>Name</th>
                <th style={th}>Roles</th>
                <th style={th}>Status</th>
                <th style={th}>Last login</th>
                <th style={{ ...th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u) => (
                <tr key={u.id} style={{ borderBottom: '0.5px solid var(--bd-0)' }}>
                  <td style={td}>{u.email}</td>
                  <td style={td}>{u.display_name}</td>
                  <td style={td}>
                    {u.roles.length === 0
                      ? <span style={{ color: 'var(--t-3)' }}>—</span>
                      : <RolesBadges roles={u.roles} />}
                  </td>
                  <td style={td}>
                    <span style={{
                      fontSize: 10, padding: '1px 6px', borderRadius: 4,
                      background: u.is_active ? 'var(--info-bg)' : 'var(--bg-3)',
                      color: u.is_active ? 'var(--info)' : 'var(--t-3)',
                      border: `0.5px solid ${u.is_active ? 'var(--info-bd)' : 'var(--bd-0)'}`,
                    }}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={td}>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button style={btnLink} onClick={() => setMode({ kind: 'edit', user: u })}>Edit</button>
                    <button style={btnLink} onClick={() => setMode({ kind: 'roles', user: u })}>Roles</button>
                  </td>
                </tr>
              ))}
              {sortedUsers.length === 0 && (
                <tr><td style={{ ...td, color: 'var(--t-3)' }} colSpan={6}>No users.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {mode.kind === 'new' && (
        <NewUserModal
          roles={roles}
          onClose={() => setMode({ kind: 'idle' })}
          onCreated={async () => { setMode({ kind: 'idle' }); await refresh() }}
        />
      )}
      {mode.kind === 'edit' && (
        <EditUserModal
          user={mode.user}
          onClose={() => setMode({ kind: 'idle' })}
          onSaved={async () => { setMode({ kind: 'idle' }); await refresh() }}
        />
      )}
      {mode.kind === 'roles' && (
        <ManageRolesModal
          user={mode.user}
          roles={roles}
          onClose={() => setMode({ kind: 'idle' })}
          onChanged={async () => { await refresh() }}
        />
      )}
    </div>
  )
}

function RolesBadges({ roles }: { roles: AdminUserSummary['roles'] }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {roles.map((r, i) => (
        <span
          key={`${r.role}/${r.resource_type ?? ''}/${r.resource_id ?? ''}/${i}`}
          style={{
            fontSize: 10, padding: '1px 6px', borderRadius: 4,
            background: 'var(--bg-3)', color: 'var(--t-1)',
            border: '0.5px solid var(--bd-0)',
            whiteSpace: 'nowrap',
          }}
          title={r.resource_type ? `${r.resource_type}: ${r.resource_id ?? ''}` : 'platform-wide'}
        >
          {r.role}
        </span>
      ))}
    </div>
  )
}

const th: React.CSSProperties = { padding: '8px 12px', fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.05em' }
const td: React.CSSProperties = { padding: '8px 12px', color: 'var(--t-1)' }
const btnPrimary: React.CSSProperties = {
  padding: '6px 10px', background: 'var(--info)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer',
}
const btnGhost: React.CSSProperties = {
  padding: '6px 10px', background: 'transparent', color: 'var(--t-1)', border: '0.5px solid var(--bd-0)', borderRadius: 6, fontSize: 12, cursor: 'pointer',
}
const btnLink: React.CSSProperties = {
  background: 'transparent', color: 'var(--info)', border: 'none', fontSize: 12, cursor: 'pointer', marginLeft: 8,
}
